import { findSong } from '~/pages/Skips/skipUtils'
import type {
	Album,
	Artist,
	Nullable,
	Playlist,
	SkipEntryPlaylist,
	SongEntries,
	SpotifyObjectType,
	Track,
	URI,
	UriObject
} from '~/types'
import { PlaylistCache } from '~/utils'

export function findPlaylist(id: string | URI): Playlist | undefined {
	const uri = getUriType(id) === 'playlist' ? id : idToUri(id, 'playlist')
	const pl = PlaylistCache.get(uri as URI<'playlist'>)
	return pl
}

export function isPlaylist(obj: SpotifyApi.ContextObject | null | undefined): obj is SpotifyApi.ContextObject {
	return obj?.type === 'playlist'
}

export function getUriType<T extends string>(uri: T): T extends URI<infer R> ? R : SpotifyObjectType {
	return uri.split(':')[1] as any
}

export function isUriType<T extends SpotifyObjectType>(uri: Nullable<UriObject>, test: T): uri is UriObject<T> {
	if (uri?.uri == undefined) return false
	return getUriType(uri.uri) === test
}

export function idToUri<T extends SpotifyObjectType>(id: string, type: T): URI<T> {
	return `spotify:${type}:${id}`
}
export function uriToId<T extends URI>(uri: T): T extends `spotify:${string}:${infer R}` ? R : never {
	return uri.split(':').pop() as any
}

type TrackMeta = SpotifyApi.PlaylistTrackObject

export function toTrack<T extends TrackMeta | SpotifyApi.TrackObjectFull>(
	t: T,
	position?: number
): T extends TrackMeta ? Track : Omit<Track, 'meta'> & { meta?: Track['meta'] } {
	const isPl = (obj: TrackMeta | SpotifyApi.TrackObjectFull): obj is TrackMeta =>
		(obj as TrackMeta).added_at !== undefined

	const track: SpotifyApi.TrackObjectFull = (isPl(t) ? t.track : (t as SpotifyApi.TrackObjectFull))!

	return {
		id: track.id,
		name: track.name,
		uri: track.uri as Track['uri'],
		artists: track.artists.map(artist => ({
			id: artist.id,
			name: artist.name,
			uri: artist.uri as Artist['uri']
		})),
		album: {
			id: track.album.id,
			name: track.album.name,
			uri: track.album.uri as Album['uri'],
			images: track.album.images
		},
		duration_ms: track.duration_ms,
		meta: isPl(t)
			? {
					added_at: t.added_at,
					added_by: t.added_by,
					is_local: t.is_local,
					index: typeof position === 'number' ? position : -1
				}
			: undefined
	} as Track
}

export function toPlaylist<T extends SpotifyApi.PlaylistObjectSimplified>(playlist: T, existing?: Playlist): Playlist {
	const current = existing || {
		tracks: {
			lastFetched: null,
			items: {},
			loaded: 0
		}
	}
	return {
		...existing,
		...playlist,
		followers: null,
		uri: playlist.uri as Playlist['uri'],
		tracks: { ...current.tracks, ...playlist.tracks },
		selected: false
	}
}

export function songEntriesToSongs(items: SongEntries) {
	return Object.keys(items)
		.map(id => findSong(id))
		.filter((t): t is Track => t !== undefined)
		.map(t => ({
			...t,
			meta: {
				...t.meta,
				plays: items[t.id]
			}
		}))
}

export function recommendedPlaylistName(playlist: SkipEntryPlaylist, playlists: Playlist[]) {
	const uris = playlist.uri.split(':')
	uris.splice(-1)
	const uri = uris.join(':')
	const result = playlists.find(pl => pl.uri == uri)
	return result ? `${result?.name}:recommended` : playlist.uri
}
